/**
 * `/rooms/invite/$code` (lane CHATWEB1): an invite link. It shows what the code opens and, when the viewer may still
 * use it, joins with it; otherwise it says why in the room's own sentence.
 */
import { useState } from 'react'
import { createFileRoute, useNavigate } from '@tanstack/react-router'
import { useQueryClient } from '@tanstack/react-query'
import { Button, Card, EmptyState, Pill, Skeleton } from '@algoboo/ui'
import { request } from '../lib/api'
import { invitePreviewQuery, newRoomKey, roomErrorCopy, upsertRoom, useInvitePreview } from '../lib/api/rooms'
import { usePageMeta } from '../lib/meta'
import { SignInPrompt, useSession } from '../components/session'
import { PRIVACY_WORDS } from '../components/rooms/GateFields'
import { RoomFace } from '../components/rooms/RoomView'
import { ROOM_REFUSAL_WORDS } from '@algoboo/schema/api'
import { prefetchSignedIn } from '../lib/route-data'
import { attempt } from '../lib/attempt'

// `rooms_`: the same URL, outside the `/rooms` layout (an invite is a page of its own, not a pane beside the list)
export const Route = createFileRoute('/rooms_/invite/$code')({
  loader: ({ context: { queryClient: qc }, params: { code } }) => prefetchSignedIn(qc, () => [/^[A-Za-z0-9_-]{12,32}$/.test(code) && qc.ensureQueryData(invitePreviewQuery(code))]),
  component: InvitePage,
})

function InvitePage() {
  usePageMeta('Room invite')
  const { code } = Route.useParams()
  const { me, loading } = useSession()
  if (loading) return <Skeleton className="h-64 rounded-xl" />
  if (!me) return <SignInPrompt title="You are invited to a room" body="Sign in to see the room this invite opens and to join it." />
  if (!/^[A-Za-z0-9_-]{12,32}$/.test(code)) return <EmptyState title="Not an invite" body="This link does not hold an invite code. Ask for a new link." />
  return <Invite code={code} />
}

function Invite({ code }: { code: string }) {
  const q = useInvitePreview(code)
  const qc = useQueryClient()
  const navigate = useNavigate()
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)
  if (q.isLoading) return <Skeleton className="h-64 rounded-xl" />
  if (q.error || !q.data) return <EmptyState title="Invite not found" body={roomErrorCopy(q.error)} />
  const { room, invite, refusal } = q.data
  const words = refusal ? ((ROOM_REFUSAL_WORDS as Record<string, string>)[refusal] ?? 'This invite cannot be used now.') : null
  return (
    <Card className="mx-auto max-w-md space-y-3 p-6 text-center" data-testid="room-invite">
      <div className="flex justify-center">
        <RoomFace room={{ ...room, peer: null }} size={64} />
      </div>
      <h1 className="font-display text-[20px] font-semibold text-ink">{room.name}</h1>
      {room.topic && <p className="text-[14px] text-ink-2">{room.topic}</p>}
      <p className="text-[13px] text-ink-3">
        <Pill tone="neutral">{PRIVACY_WORDS[room.privacy].label}</Pill> {room.memberCount === 1 ? '1 member' : `${room.memberCount.toLocaleString('en-US')} members`}
        {invite.maxUses !== null ? ` · used ${invite.uses} of ${invite.maxUses}` : ''}
      </p>
      {words && (
        <p role="alert" className="text-[13.5px] text-danger">
          {words}
        </p>
      )}
      {error && (
        <p role="alert" className="text-[13.5px] text-danger">
          {error}
        </p>
      )}
      <Button
        variant="primary"
        className="w-full"
        loading={busy}
        disabled={!!refusal}
        onClick={async () => {
          setBusy(true)
          setError(null)
          await attempt(
            async () => {
              const r = await request('joinRoom', { params: { id: room.id }, body: { inviteCode: code }, idempotencyKey: newRoomKey() })
              upsertRoom(qc, r)
              void navigate({ to: '/rooms/$roomId', params: { roomId: r.id } })
            },
            (e) => {
              setError(roomErrorCopy(e))
            },
          )
          setBusy(false)
        }}
      >
        Join the room
      </Button>
    </Card>
  )
}
